import { fmtMoney, fmtValue } from "@/lib/api";
import { personSlots } from "@/components/IdentityStep";

function identityRows(data) {
  return personSlots(data)
    .filter(([p]) => data[`${p}_cognome`] || data[`${p}_nome`])
    .map(([p, label]) => ({
      label: label + (p === "id1" && data.id1_relazione && data.fest_minore === "Sì" && data.evento_tipo === "Compleanno" ? ` • ${data.id1_relazione}` : ""),
      nome: `${data[`${p}_nome`] || ""} ${data[`${p}_cognome`] || ""}`.trim(),
      cf: data[`${p}_cf`],
      nascita: `${data[`${p}_luogo_nascita`] || ""} il ${fmtValue({ type: "date" }, data[`${p}_data_nascita`])}`,
      documento: [data[`${p}_doc_tipo`], data[`${p}_doc_numero`] && `n. ${data[`${p}_doc_numero`]}`, data[`${p}_doc_scadenza`] && `scad. ${fmtValue({ type: "date" }, data[`${p}_doc_scadenza`])}`].filter(Boolean).join(" "),
      files: (data[`${p}_files`] || []).map((f) => f.name).join(", "),
    }));
}

const Pair = ({ label, value, wide }) => (
  <div className={`${wide ? "sm:col-span-2" : ""} border-b border-dashed border-[#D4E6EC] pb-1.5`}>
    <div className="text-[10px] uppercase tracking-wider text-[#7B929C]">{label}</div>
    <div className="text-sm text-[#0D2B3A] whitespace-pre-wrap min-h-[1.25rem]">{value || <span className="text-[#A8C6CF]">—</span>}</div>
  </div>
);

const Block = ({ title, children }) => (
  <div className="mb-5">
    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white bg-[#0B6E8F] px-3 py-1.5 rounded-t-md">{title}</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 p-3 border border-t-0 border-[#D4E6EC] rounded-b-md">{children}</div>
  </div>
);

const Notes = ({ notes }) => (notes || []).map((n) => <p key={n} className="text-xs italic text-[#47606B] mt-3 leading-relaxed">{n}</p>);

function Header({ subtitle, title }) {
  return (
    <div className="mb-5">
      <div className="font-serif-it text-2xl sm:text-3xl font-semibold text-[#0B6E8F]">{subtitle}</div>
      <div className="text-[11px] tracking-wider text-[#7B929C] mt-1">{title}</div>
    </div>
  );
}

export function SheetView({ page, data, template }) {
  const d = (k, t = "date") => fmtValue({ type: t }, data[k]);
  const ids = page.key === "p1" ? identityRows(data) : [];
  return (
    <article className="paper rounded-xl border border-[#D4E6EC] p-5 sm:p-8 shadow-sm" data-testid={`sheet-view-${page.key}`}>
      <Header subtitle={page.subtitle} title={page.title} />
      {page.intro && <p className="text-xs italic text-[#47606B] mb-4">{page.intro}</p>}
      {page.key === "p5" && (
        <Block title="Riferimenti evento (da Scheda)">
          <Pair label="Cliente / evento" value={`${data.cliente_nome || ""} • ${data.evento_tipo || ""}`} />
          <Pair label="Data" value={d("evento_data")} />
          <Pair label="Adulti minimi" value={`${data.min_adulti || ""} a ${d("prezzo_adulti", "money")}`} />
          <Pair label="Bambini minimi" value={`${data.min_bambini || ""} a ${d("prezzo_bambini", "money")}`} />
        </Block>
      )}
      {(page.sections || []).filter((s) => !(s.pianob && /AFRODITE/i.test(data.sede || ""))).map((s, i) => (
        <div key={s.title}>
          <Block title={s.title}>
            {(s.fixed || []).map(([a, b]) => <Pair key={a} label={a} value={b} wide />)}
            {s.fields.filter((f) => !(f.hide_empty && !data[f.k])).map((f) => <Pair key={f.k} label={f.label} value={fmtValue(f, data[f.k])} wide={f.wide} />)}
          </Block>
          {i === 0 && ids.length > 0 && (
            <Block title="Identificazione dei committenti">
              {ids.map((r) => <Pair key={r.label} wide label={r.label} value={`${r.nome} • C.F. ${r.cf || "—"}\nNato/a a ${r.nascita}\n${r.documento}${r.files ? "\nAllegato: " + r.files : ""}`} />)}
              {data.evento_tipo === "Compleanno" && data.fest_minore === "Sì" && <p className="sm:col-span-2 text-xs italic text-[#47606B]">{template.minor_declaration}</p>}
            </Block>
          )}
        </div>
      ))}
      {page.articles && template.articles[page.articles].map((a) => (
        <p key={a.n} className="text-[13px] leading-relaxed text-[#0D2B3A] mb-3 text-justify"><b>{a.n}. {a.title}</b> {a.text}</p>
      ))}
      <Notes notes={page.notes} />
      {page.key === "p5" && (data.a_menu_files || []).length > 0 && (
        <div className="mt-4 rounded-lg border-2 border-[#34B3B0] bg-[#EAF5F7] p-4 text-sm" data-testid="sheet-menu-files">
          <b>Menù allegato in originale:</b> {data.a_menu_files.map((f) => f.name).join(", ")}. Il file costituisce parte integrante del presente Allegato A.
        </div>
      )}
      {page.specific && (
        <div className="mt-5 rounded-lg border-2 border-[#34B3B0] bg-[#EAF5F7] p-4" data-testid="specific-approval-box">
          <div className="font-semibold text-sm text-[#0B6E8F]">{page.specific.title}</div>
          <p className="text-[13px] mt-1">{page.specific.text}</p>
        </div>
      )}
    </article>
  );
}

export function ExtraSheetView({ extra, data, template }) {
  const ex = template.extra;
  const total = (extra.rows || []).reduce((t, r) => t + Number(r.quantita || 0) * Number(r.unitario || 0), 0);
  return (
    <article className="paper rounded-xl border border-[#D4E6EC] p-5 sm:p-8 shadow-sm" data-testid={`sheet-view-extra-${extra.numero}`}>
      <Header subtitle={`${ex.subtitle} • N. ${extra.numero}`} title={ex.title} />
      <Block title="Evento">
        <Pair label="Evento" value={`${data.cliente_nome || ""} • ${data.evento_tipo || ""}`} />
        <Pair label="Data evento" value={fmtValue({ type: "date" }, data.evento_data)} />
      </Block>
      {ex.sections.map((s) => (
        <Block key={s.title} title={s.title}>
          {s.fields.map((f) => <Pair key={f.k} label={f.label} value={fmtValue(f, (extra.data || {})[f.k])} wide={f.wide} />)}
        </Block>
      ))}
      <table className="w-full text-sm border border-[#D4E6EC] mb-2">
        <thead className="bg-[#0B6E8F] text-white text-[11px] uppercase tracking-wider">
          <tr><th className="text-left p-2">Servizio</th><th className="p-2">Q.tà</th><th className="p-2">Unitario</th><th className="p-2">Totale</th></tr>
        </thead>
        <tbody>
          {(extra.rows || []).map((r, i) => (
            <tr key={i} className="border-t border-[#D4E6EC]"><td className="p-2">{r.servizio}</td><td className="p-2 text-center">{r.quantita}</td><td className="p-2 text-right">{fmtMoney(r.unitario || 0)}</td><td className="p-2 text-right">{fmtMoney(Number(r.quantita || 0) * Number(r.unitario || 0))}</td></tr>
          ))}
          <tr className="border-t border-[#D4E6EC] font-semibold"><td className="p-2" colSpan={3}>TOTALE EXTRA, imposte incluse se dovute</td><td className="p-2 text-right" data-testid="extra-sheet-total">{fmtMoney(total)}</td></tr>
        </tbody>
      </table>
      <Notes notes={ex.notes} />
    </article>
  );
}
